import React, { useEffect, useState, useRef, useCallback, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchNewsByDateRange, NewsListItems, sortNewsArticles } from "./NewsAPIUtil";
import { useTranslations } from "src/contexts/TranslationContext";
import RobotsNoIndex from "src/components/seo/RobotsNoIndex";
import { MainStoryVideo, SecondaryStoryVideo } from './RenderStoryVideo';
import { processFootnotes, getTranslatedType } from "./NewsListHelper";
import ArchiveSection from './ArchiveSection';
import useResponsiveArticles from './hooks/useResponsiveArticles';
interface NewsListProps {
    type?: string;
    subtype?: string;
}

const NewsList: React.FC<NewsListProps> = ({ type: propType = 'trending', subtype: propSubtype }) => {
    // Read the type, subtype and page parameters from URL query
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const queryType = queryParams.get('type');
    const querySubtype = queryParams.get('subtype');
    const queryPage = parseInt(queryParams.get('page') || '1', 10);

    // Extract type/subtype from URL path as well (for category navigation)
    const pathParts = location.pathname.split('/').filter(Boolean);
    // Skip language prefix if present
    const startIndex = pathParts[0]?.match(/^(en|es|fr|cn|tw)$/) ? 1 : 0;
    const pathType = pathParts[startIndex];
    const pathSubtype = pathParts[startIndex + 1];

    // Use the type and subtype from URL path or query or props
    const finalType = pathType || queryType || propType;
    const finalSubtype = pathSubtype || querySubtype || propSubtype;

    const now = Math.floor(Date.now()); // current time in mili-seconds
    const oneYearAgo = now - 365 * 24 * 60 * 60 * 1000; // mili-seconds in a year, java will convert it to seconds

      // Set the minimum trending score based on the type and URL
    const minTrendingScore = finalType === 'trending' ? 60 : 10;
    console.log(`Setting minTrendingScore for ${finalType} to ${minTrendingScore}`);

    // When type is 'trending', use null instead for API calls (don't filter by type)
    const apiType = finalType === 'trending' ? null : finalType;
    console.log(`Setting apiType for ${finalType} to ${apiType !== null ? apiType : 'null'}`);

    // Use the translation context to get the current language and translations
    const { language, getUrlLanguagePrefix, translations: t } = useTranslations();
    console.log(`Current language: ${language}`);

    const [news, setNews] = useState<NewsListItems[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [embedKey, setEmbedKey] = useState(0);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const navigate = useNavigate();
    const observer = useRef<IntersectionObserver | null>(null);

    // Store the last pathname to detect navigation between categories
    const lastPathname = useRef<string>(location.pathname);

    // Create a ref to track the loading status to prevent duplicate calls
    const isLoadingRef = useRef<boolean>(false);
    // Track the last loaded parameters to avoid duplicate calls
    const lastLoadedParamsRef = useRef<string>('');
    // Keep track of loaded pages to avoid duplicate content
    const loadedPagesRef = useRef<Set<number>>(new Set([1]));

    // Maximum number of pages before showing archive link
    const MAX_PAGES = 12;

    // number of articles load each time
    // minimum is 5, less than 6, it will triger load for next page in larger screen.
    const NUMBER_OF_ARTICLES = useResponsiveArticles();
    console.log("width:",window.innerWidth,"NUMBER_OF_ARTICLES:", NUMBER_OF_ARTICLES);

    console.log("NewsList using type:", finalType, "subtype:", finalSubtype, "and language:", language);

    // Function to update URL without reloading
    const updateUrlWithPage = (newPage: number) => {
        // Only update URL parameter, don't reset the view or news list
        const newParams = new URLSearchParams(location.search);
        newParams.set('page', newPage.toString());
        navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    };

    const loadMoreNews = useCallback(async () => {
        // Debug log: entering loadMoreNews
        console.log('[loadMoreNews] called. loadingMore:', loadingMore, 'hasMore:', hasMore, 'currentPage:', currentPage, 'MAX_PAGES:', MAX_PAGES, 'news.length:', news.length);
        // Skip if already loading, no more results, or reached max pages
        if (loadingMore || !hasMore || currentPage >= MAX_PAGES) {
            console.log('[loadMoreNews] Skipping: loadingMore:', loadingMore, 'hasMore:', hasMore, 'currentPage:', currentPage, 'MAX_PAGES:', MAX_PAGES);
            return;
        }

        const nextPage = currentPage + 1;

        // Skip if we've already loaded this page
        if (loadedPagesRef.current.has(nextPage)) {
            console.log(`[loadMoreNews] Page ${nextPage} already loaded, skipping`);
            return;
        }

        const loadMoreKey = `${language}:${finalType}:${finalSubtype || 'all'}:page${nextPage}`;

        if (isLoadingRef.current && lastLoadedParamsRef.current === loadMoreKey) {
            console.log(`[loadMoreNews] Skipping duplicate load for ${loadMoreKey}`);
            return;
        }

        setLoadingMore(true);
        setCurrentPage(nextPage);
        updateUrlWithPage(nextPage);

        isLoadingRef.current = true;
        lastLoadedParamsRef.current = loadMoreKey;

        try {
            const apiParams = {
                startTime: oneYearAgo,
                endTime:now,
                page: nextPage - 1, // API uses 0-based indexing
                size: NUMBER_OF_ARTICLES,
                lang: language,
                type: apiType, // Use apiType instead of finalType
                subtype: finalSubtype,
                minTrendingScore: minTrendingScore
            };
            console.log('[loadMoreNews] API call params:', apiParams);

            const newNews = await fetchNewsByDateRange(apiParams);

            console.log('[loadMoreNews] Received more news data:', newNews, 'length:', Array.isArray(newNews) ? newNews.length : 'not array');

            // Validate the response
            if (!newNews || !Array.isArray(newNews)) {
                console.error('[loadMoreNews] Invalid news data received:', newNews);
                setHasMore(false);
                return;
            }

            // Filter out invalid entries and sort
            const validNews = sortNewsArticles(newNews.filter(item => item.title && item.title.length >= 5));
            console.log('[loadMoreNews] validNews.length:', validNews.length);

            if (validNews.length > 0) {
                // Clean the data by removing footnote references from titles
                validNews.forEach(item => {
                    if (item.title) {
                        item.title = item.title.replace(/\[\^?\d+\](?:\[\^?\d+\])*/g, '');
                    }
                });

                // Filter out any duplicates based on ID
                const existingIds = new Set(news.map(item => item.id));
                const uniqueNewNews = validNews.filter(item => !existingIds.has(item.id));

                if (uniqueNewNews.length > 0) {
                    // Append the new news to the existing news list
                    setNews(prevNews => [...prevNews, ...uniqueNewNews]);

                    // Mark this page as loaded
                    loadedPagesRef.current.add(nextPage);

                    // If we've reached max pages or got less than requested, there's no more
                    // const willHaveMore = validNews.length === NUMBER_OF_ARTICLES && nextPage < MAX_PAGES;
                    const willHaveMore = validNews.length > 0 && nextPage < MAX_PAGES;
                    setHasMore(willHaveMore);
                    console.log('[loadMoreNews] Setting hasMore to', willHaveMore, 'because validNews.length:', validNews.length, 'NUMBER_OF_ARTICLES:', NUMBER_OF_ARTICLES, 'nextPage:', nextPage, 'MAX_PAGES:', MAX_PAGES);
                } else {
                    setHasMore(nextPage < MAX_PAGES);
                    console.log('[loadMoreNews] Setting hasMore to', nextPage < MAX_PAGES, 'because no unique new news');
                }
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('[loadMoreNews] Error loading more news:', error);
            setHasMore(false);
        } finally {
            setLoadingMore(false);
            isLoadingRef.current = false;
        }
    }, [language, currentPage, hasMore, loadingMore, finalType, finalSubtype, apiType, location.pathname, location.search, navigate, minTrendingScore, news]);

    const lastNewsElementRef = useCallback((node: HTMLDivElement | null) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            console.log('lastNewsElementRef loadingMore', loadingMore);
            console.log('lastNewsElementRef currentPage', currentPage);
            console.log('lastNewsElementRef MAX_PAGES', MAX_PAGES);
            console.log('lastNewsElementRef news.length', news.length);
            console.log('lastNewsElementRef secondaryStories.length', news.length > 1 ? news.length - 1 : 0);
            if (entries[0].isIntersecting && hasMore && !loadingMore && currentPage < MAX_PAGES) {
                console.log('lastNewsElementRef: Triggering loadMoreNews');
                loadMoreNews();
            }
        }, {
            root: null,
            rootMargin: '100px',
            threshold: 0.1,
        });

        if (node) observer.current.observe(node);
    }, [loading, loadMoreNews, hasMore, loadingMore, currentPage, news.length]);

    // Scroll to top handler
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Handle scroll events for back to top button
    useEffect(() => {
        const handleScroll = () => {
            // Show back to top button after scrolling down 300px
            setShowBackToTop(window.scrollY > 300);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Initial data load when component mounts or dependencies change
    useEffect(() => {
        const pathChanged = location.pathname !== lastPathname.current;

        // Update lastPathname ref
        lastPathname.current = location.pathname;

        // If the path changed (navigated between categories), force a reload
        const shouldForceReload = pathChanged;

        // If path changed, don't skip loading even if query page is already loaded
        if (shouldForceReload || !(initialLoadComplete && loadedPagesRef.current.has(queryPage))) {
            // Reset everything when navigating between categories
            if (shouldForceReload) {
                console.log("Path changed, resetting and reloading data");
                setNews([]);
                loadedPagesRef.current = new Set([1]);
                setCurrentPage(1);
                setLoading(true);
                setHasMore(true);
                setInitialLoadComplete(false);
            } else if (initialLoadComplete) {
                console.log(`Page ${queryPage} already loaded, just updating current page state`);
                setCurrentPage(queryPage);
                return;
            }

            // Create a unique key for current params to prevent redundant loads
            const currentParamsKey = `${language}:${finalType}:${finalSubtype || 'all'}:initial:${location.pathname}`;

            // Skip if we're already loading the same data
            if (isLoadingRef.current && lastLoadedParamsRef.current === currentParamsKey) {
                console.log(`Skipping duplicate load for ${currentParamsKey}`);
                return;
            }

            console.log("Language, type, or subtype changed:", language, finalType, finalSubtype, "minTrendingScore:", minTrendingScore);
            console.log(`Making API call with type: ${apiType} (original: ${finalType})`);

            if (!shouldForceReload) {
                setLoading(true);
            }

            // Mark as loading and remember params
            isLoadingRef.current = true;
            lastLoadedParamsRef.current = currentParamsKey;

            const initialLoadNews = async () => {
                try {
                    console.log(`Initial news load with type: ${apiType} (original: ${finalType}), minTrendingScore: ${minTrendingScore}, language: ${language}`);
                    const apiParams = {
                        startTime: oneYearAgo,
                        endTime: now,
                        page: 0, // API uses 0-based indexing
                        size: NUMBER_OF_ARTICLES,
                        lang: language,
                        type: apiType, // Use apiType instead of finalType
                        subtype: finalSubtype,
                        minTrendingScore: minTrendingScore
                    };
                    console.log('Initial API call params:', apiParams);

                    const initialNews = await fetchNewsByDateRange(apiParams);

                    console.log('[initialLoadNews] Received initial news data:', initialNews, 'length:', Array.isArray(initialNews) ? initialNews.length : 'not array');

                    // Validate the response
                    if (!initialNews || !Array.isArray(initialNews)) {
                        console.error('[initialLoadNews] Invalid news data received:', initialNews);
                        setNews([]);
                        setHasMore(false);
                        return;
                    }

                    // Filter out invalid entries and sort
                    const validNews = sortNewsArticles(initialNews.filter(item => item.title && item.title.length >= 5));
                    console.log('[initialLoadNews] validNews.length:', validNews.length);

                    // Clean the data by removing footnote references from titles
                    validNews.forEach(item => {
                        if (item.title) {
                            item.title = item.title.replace(/\[\^?\d+\](?:\[\^?\d+\])*/g, '');
                        }
                    });

                    console.log("Processed and cleaned news data:", validNews);

                    setNews(validNews);
                    setHasMore(initialNews.length === NUMBER_OF_ARTICLES); // If we got less than requested, there's no more
                    setInitialLoadComplete(true);

                    // If URL has a page > 1, load additional pages
                    if (queryPage > 1 && validNews.length > 0) {
                        for (let i = 2; i <= queryPage; i++) {
                            // Wait a moment between requests to avoid overwhelming the server
                            await new Promise(resolve => setTimeout(resolve, 300));

                            // Set current page to i-1 so the loadMoreNews call will load page i
                            setCurrentPage(i - 1);
                            await loadMoreNews();
                        }
                    }
                } catch (error) {
                    console.error("Error loading news:", error);
                    setNews([]);
                    setHasMore(false);
                } finally {
                    setLoading(false);
                    setEmbedKey(prev => prev + 1);
                    // Reset loading flag
                    isLoadingRef.current = false;
                    setInitialLoadComplete(true);
                }
            };

            initialLoadNews();
        }
    }, [language, finalType, finalSubtype, apiType, minTrendingScore, loadMoreNews, location.pathname, queryPage]);

    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://platform.twitter.com/widgets.js";
        script.async = true;
        document.body.appendChild(script);
        return () => { script.remove(); };
    }, [embedKey]);

    if (loading) return <p className="text-center text-gray-600">Loading initial news...</p>;

    // Only show the first news item as the main story
    const mainStory = news.length > 0 ? news[0] : null;
    const secondaryStories = news.length > 1 ? news.slice(1) : [];

    const handleNewsClick = (id: string) => {
        const prefix = getUrlLanguagePrefix(language);
        navigate(`${prefix}/trendingnews/${id}`);
    };

    // const imageUrl = 'https://pbs.twimg.com/media/GnkwSFDWcAAGzaw.jpg';





    return (
        <div className="max-w-7xl mx-auto px-1 min-h-screen relative pb-20">
            {/* Add robots noindex meta tag when a subtype is present */}
            {finalSubtype && <RobotsNoIndex />}

            {loading ? (
                <div className="text-center py-8">Loading...</div>
            ) : news.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-gray-500 mb-8">
                        {processFootnotes(t?.navigations?.header?.noNewsAvailable || 'No news available for this category.')}
                    </p>
                </div>
            ) : (
                <>
                    {mainStory && (
                        <div className="mb-8">
                            <div className="bg-white rounded-lg overflow-hidden shadow-lg h-full">
                                <MainStoryVideo
                                    embedHtml={mainStory.video_embed_link || ''}
                                    keyId={mainStory.id}
                                    title={mainStory.title}
                                    postTime={mainStory.postTime}
                                    img_urls={mainStory.img_urls || []}
                                    type={mainStory.type || finalType}
                                    subtype={mainStory.subtype || ''}
                                    t={t}
                                    language={language}
                                    getUrlLanguagePrefix={getUrlLanguagePrefix}
                                    handleNewsClick={handleNewsClick}
                                    navigate={navigate}
                                />
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ">
                        {news.slice(1).map((article, index) => (
                            <div
                                key={article.id}
                                ref={index === news.length - 2 ? lastNewsElementRef : null}
                                className="bg-white rounded-lg overflow-hidden shadow-lg h-full"
                            >
                                <div className="p-6">
                                    <h2
                                        onClick={() => handleNewsClick(article.id)}
                                        className="text-2xl font-extrabold text-blue-900 cursor-pointer hover:text-blue-950 transition-colors line-clamp-2 mb-3 font-serif tracking-tight leading-tight"
                                    >
                                        {processFootnotes(article.title)}
                                        {article.subtype && (
                                            <span className="block text-lg font-medium text-blue-600 mt-1">
                                                {getTranslatedType(article.subtype, t)}
                                            </span>
                                        )}
                                    </h2>
                                    <p className="text-gray-600 mt-2 text-sm">
                                        {new Date(article.postTime * 1000).toLocaleDateString()}
                                        <span className="mx-2">•</span>
                                        {article.type && (
                                            <span
                                                className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium hover:underline inline-flex items-center"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    const path = `/${article.type}`;
                                                    const fullPath = `${getUrlLanguagePrefix(language)}${path}`;
                                                    navigate(fullPath);
                                                }}
                                            >
                                                {getTranslatedType(article.type, t)}
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                                </svg>
                                            </span>
                                        )}
                                        {article.type && article.subtype && " > "}
                                        {article.subtype && (
                                            <span
                                                className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium hover:underline inline-flex items-center"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    const path = `/${article.type}/${article.subtype}`;
                                                    const fullPath = `${getUrlLanguagePrefix(language)}${path}`;
                                                    navigate(fullPath);
                                                }}
                                            >
                                                {getTranslatedType(article.subtype, t)}
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                                </svg>
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <div>

                                    <SecondaryStoryVideo
                                        embedHtml={article.video_embed_link || ''}
                                        keyId={article.id}
                                        img_urls={article.img_urls || []}
                                        handleNewsClick={handleNewsClick}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>


              
                </>
            )}

           <ArchiveSection
                getUrlLanguagePrefix={getUrlLanguagePrefix}
                language={language}
                finalType={finalType}
                finalSubtype={finalSubtype}
                navigate={navigate}
                t={t}
                showBackToTop={showBackToTop}
                scrollToTop={scrollToTop}
            />

        </div>
    );
};

export default NewsList;